import { z } from "zod";
import { DunningLevel, DunningSchema, AddressSchema } from "../schemas";
import { FinanceBalanceRecordSchema } from "./FinanceBalances";
export const ReminderDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const d = new Date(value + "T00:00:00Z");
    return (
      Number.isFinite(d.getTime()) && d.toISOString().slice(0, 10) === value
    );
  });
export const ReminderBlockSchema = z.enum([
  "INVOICE",
  "BALANCE",
  "ACTIVE",
  "COOLDOWN",
  "HISTORY",
  "LIMIT",
  "PARTIES",
  "NUMBER_RANGE",
]);
export const ReminderPartySchema = z.object({
  name: z.string().trim().min(1),
  address: AddressSchema,
});
export const ReminderHistorySchema = z.object({
  id: z.string(),
  documentNumber: z.string().nullable(),
  status: z.string().nullable(),
  issueDate: z.string().nullable(),
  sentAt: z.string().nullable(),
  level: z.string().nullable(),
});
export const ReminderReviewSchema = z.object({
  tenantId: z.string().min(1),
  invoiceId: z.string().min(1),
  invoiceNumber: z.string(),
  asOf: z.string().datetime(),
  reviewRevision: z.string().regex(/^reminder-[a-f0-9]{64}$/),
  balance: FinanceBalanceRecordSchema.nullable(),
  sender: ReminderPartySchema.nullable(),
  recipient: ReminderPartySchema.nullable(),
  history: z.array(ReminderHistorySchema).max(200),
  blockers: z.array(ReminderBlockSchema),
  canPrepare: z.boolean(),
  proposal: z
    .object({
      level: DunningLevel,
      dueDate: ReminderDateSchema,
      feeAmount: z.literal(0),
    })
    .nullable(),
});
export const ReminderPrepareSchema = z
  .object({
    invoiceId: z.string().min(1).max(200),
    reviewRevision: z.string().regex(/^reminder-[a-f0-9]{64}$/),
    dueDate: ReminderDateSchema,
    feeAmount: z.number().finite().nonnegative(),
    feeDescription: z.string().trim().max(200),
    notes: z.string().trim().max(2000),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.feeAmount > 0 && !data.feeDescription)
      ctx.addIssue({
        code: "custom",
        path: ["feeDescription"],
        message: "Describe the fee.",
      });
  });
export const ReminderReceiptSchema = z.object({
  tenantId: z.string().min(1),
  actorId: z.string().min(1),
  attemptKey: z.string().uuid(),
  invoiceId: z.string().min(1),
  documentId: z.string().uuid(),
  documentNumber: z.string().min(1),
  createdAt: z.string().datetime(),
  status: z.literal("DRAFT"),
  data: DunningSchema,
});
export type ReminderReview = z.infer<typeof ReminderReviewSchema>;
export type ReminderPrepare = z.infer<typeof ReminderPrepareSchema>;
export type ReminderReceipt = z.infer<typeof ReminderReceiptSchema>;
